import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';
import { AccessStatus, StaffResponse } from '../../Shared/types/types';

export interface CreateTeacherRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  role: 'teacher' | 'staff';
}

export interface CreateTeacherResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  accessStatus: AccessStatus;
  inviteChannel?: 'email' | 'sms' | 'whatsapp';
  user: {
    id: number;
    roles?: string[];
    otpDeliveryMethod?: string | null;
    isActive?: boolean;
  };
}

export const staffsApi = createApi({
  reducerPath: 'staffsApi',
  baseQuery,
  tagTypes: ['Staff'],
  endpoints: builder => ({
    getStaffs: builder.query<
      StaffResponse,
      { search?: string; gender?: string; userId?: string }
    >({
      query: ({ search, gender, userId }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (gender) params.append('gender', gender);
        if (userId) params.append('user_id', userId);
        if (!params.toString()) return '/enrollment/staff';
        return `/enrollment/staff?${params.toString()}`;
      },
      providesTags: ['Staff'],
    }),
    getStaff: builder.query<any, string>({
      query: id => `/enrollment/staff/${id}`,
      providesTags: ['Staff'],
    }),
    getStaffByUserId: builder.query<any, string>({
      query: userId => {
        const params = new URLSearchParams();
        if (userId) params.append('userId', userId);
        return `/enrollment/staff?${params.toString()}`;
      },
      providesTags: ['Staff'],
      transformResponse: (response: StaffResponse) => {
        return response.data ? response.data[0] : null;
      },
    }),
    createStaff: builder.mutation<any, any>({
      query: newStaff => ({
        url: '/enrollment/staff',
        method: 'POST',
        body: newStaff,
      }),
      invalidatesTags: ['Staff'],
    }),
    createTeacher: builder.mutation<
      CreateTeacherResponse,
      CreateTeacherRequest
    >({
      query: body => ({
        url: '/enrollment/staff/invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Staff'],
    }),
    updateStaff: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/enrollment/staff/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['Staff'],
    }),
    setStaffStatus: builder.mutation<any, { id: number; status: string }>({
      query: ({ id, status }) => ({
        url: `/enrollment/staff/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Staff'],
    }),
    resendStaffInvite: builder.mutation<unknown, number>({
      query: id => ({
        url: `/enrollment/staff/${id}/invite/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Staff'],
    }),
    deleteStaff: builder.mutation<void, number>({
      query: id => ({
        url: `/enrollment/staff/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Staff'],
    }),
  }),
});

export const {
  useGetStaffsQuery,
  useGetStaffQuery,
  useGetStaffByUserIdQuery,
  useCreateStaffMutation,
  useCreateTeacherMutation,
  useUpdateStaffMutation,
  useSetStaffStatusMutation,
  useDeleteStaffMutation,
  useResendStaffInviteMutation,
} = staffsApi;
